import { useEffect, useRef, useState } from 'react';
import { Image, Transformer } from 'react-konva';
import useImage from 'use-image';



const CanvasItem = ({
  item,
  selectedItemId,
  setSelectedItemId,
  handleItemUpdate,
  handleItemDelete,
  canvasType,
  canvasBounds,
  drawingMode = false,
}) => {
  const shapeRef = useRef(null);
  const transformerRef = useRef(null);
  const [img] = useImage(item.imageUrl, 'anonymous');
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Set initial width/height from image
  useEffect(() => {
    if (img && (!item.width || !item.height)) {
      handleItemUpdate(
        item.id,
        { width: img.width, height: img.height },
        canvasType
      );
    }
  }, [img]);

  // Attach transformer when selected
  useEffect(() => {
    if (
      selectedItemId === item.id &&
      transformerRef.current &&
      shapeRef.current &&
      !drawingMode &&
    !item.locked // ADD THIS CONDITION
    ) {
      transformerRef.current.nodes([shapeRef.current]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [selectedItemId, drawingMode, item.locked]);

  // Delete on keydown
  useEffect(() => {
    if (selectedItemId !== item.id || drawingMode) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        handleItemDelete(item.id, canvasType);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedItemId, drawingMode, item.locked]);

  // Mouse down → start dragging with offset
  const handleMouseDown = (e) => {
    if (drawingMode || item.locked) return; // 🚫 Do nothing when drawing

    e.cancelBubble = true;
    e.evt.stopPropagation();

    const stage = e.target.getStage();
    const pointerPos = stage.getPointerPosition();

    dragOffset.current = {
      x: pointerPos.x - item.position.x,
      y: pointerPos.y - item.position.y,
    };

    setIsDragging(true);
    setSelectedItemId(item.id);
    handleItemUpdate(item.id, { isSelected: true }, canvasType);
  };

  // Mouse move → apply dragging with bounds

  // const handleMouseMove = (e) => {
  //   if (!isDragging || !shapeRef.current) return;

  //   const stage = shapeRef.current.getStage();
  //   const pointerPos = stage.getPointerPosition();
  //   if (!pointerPos) return;

  //   let newX = pointerPos.x - dragOffset.current.x;
  //   let newY = pointerPos.y - dragOffset.current.y;

  //   newX = Math.max(0, Math.min(newX, canvasBounds.width - item.width));
  //   newY = Math.max(0, Math.min(newY, canvasBounds.height - item.height));

  //   shapeRef.current.position({ x: newX, y: newY });
  //   shapeRef.current.getLayer().batchDraw();
  // };

  // Mouse move → apply dragging with rotated bounds
// Mouse move → apply dragging with rotated bounds + 25px overflow
const handleMouseMove = (e) => {
  if (!isDragging || !shapeRef.current) return;

  const stage = shapeRef.current.getStage();
  const pointerPos = stage.getPointerPosition();
  if (!pointerPos) return;

  let newX = pointerPos.x - dragOffset.current.x;
  let newY = pointerPos.y - dragOffset.current.y;

  // temp move
  shapeRef.current.position({ x: newX, y: newY });

  // get rotated bounding box
  const box = shapeRef.current.getClientRect();
  const { width: cW, height: cH } = canvasBounds;

  // 👇 allow 25px overflow outside each edge
  const margin = 25;
  const correctedPos = { x: newX, y: newY };

  if (box.x < -margin) correctedPos.x -= box.x + margin;
  if (box.y < -margin) correctedPos.y -= box.y + margin;
  if (box.x + box.width > cW + margin)
    correctedPos.x -= box.x + box.width - (cW + margin);
  if (box.y + box.height > cH + margin)
    correctedPos.y -= box.y + box.height - (cH + margin);

  shapeRef.current.position(correctedPos);
  shapeRef.current.getLayer().batchDraw();

  // 🔎 Debug log
  console.log("DEBUG Drag with margin", {
    pointer: pointerPos,
    attempted: { x: newX, y: newY },
    corrected: correctedPos,
    box,
    bounds: canvasBounds,
    margin
  });
};


  // Mouse up → finalize drag
  const handleMouseUp = () => {
    if (isDragging && shapeRef.current) {
      const node = shapeRef.current;
      handleItemUpdate(
        item.id,
        {
          position: { x: node.x(), y: node.y() },
          isSelected: true,
        },
        canvasType
      );
    }
    setIsDragging(false);
  };

  // Global listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  // Handle resizing/rotation
  const handleTransformEnd = () => {
    if (drawingMode || !shapeRef.current || item.locked) return;

    const node = shapeRef.current;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    const newWidth = Math.max(5, item.width * scaleX);
    const newHeight = Math.max(5, item.height * scaleY);

    node.scaleX(1);
    node.scaleY(1);

    handleItemUpdate(
      item.id,
      {
        position: { x: node.x(), y: node.y() },
        width: newWidth,
        height: newHeight,
        rotate: node.rotation(),
      },
      canvasType
    );
  };

  return (
    <>
      <Image
        ref={shapeRef}
        name="canvas-item"
        image={img}
        x={item.position.x}
        y={item.position.y}
        width={item.width}
        height={item.height}
        rotation={item.rotate}
        draggable={false} // we handle dragging manually
        onMouseDown={!drawingMode ? handleMouseDown : undefined} // 🚫 disable click when drawing
        onTransformEnd={handleTransformEnd}
        visible={item.visible}
        listening={!drawingMode} // 🚫 no events at all when drawing
        perfectDrawEnabled={false}
        opacity={item.locked ? 0.7 : 1} // ADD THIS LINE
      />
      {selectedItemId === item.id && !drawingMode && !item.locked && (
        <Transformer
          ref={transformerRef}
          rotateEnabled={true}
          enabledAnchors={[
            'top-left', 'top-center', 'top-right',
            'middle-left', 'middle-right',
            'bottom-left', 'bottom-center', 'bottom-right',
          ]}
          anchorSize={6}
          ignoreStroke={true}
          perfectDrawEnabled={false}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 1 || newBox.height < 1) {
              return oldBox;
            }
            return newBox;
          }}
        />
      )}
    </>
  );
};

export default CanvasItem;